/**
 * PageErrorBoundary — wraps the routed page inside ProtectedRoute.
 *
 * Why this exists: a render crash inside a page (e.g. an invalid date on the
 * job detail screen) previously bubbled to SubscriptionGateErrorBoundary,
 * whose fallback re-rendered the same page — producing a remount loop that
 * looked to the user like a permanent "Loading job details..." spinner with
 * flickering navigation. Containing the crash at page level gives the user a
 * definitive error state with a retry, and keeps navigation stable.
 */
import { Component, ReactNode } from "react";
import { AlertCircle, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureError } from "@/lib/observability";

interface Props {
  children: ReactNode;
  /** Changing this (route path) clears a previous crash. */
  routeKey?: string;
}

interface State {
  hasError: boolean;
  routeKey?: string;
}

export class PageErrorBoundary extends Component<Props, State> {
  /** Report a crash once per mount so a crash can never create an error storm. */
  private reported = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, routeKey: props.routeKey };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.routeKey !== state.routeKey) {
      return { hasError: false, routeKey: props.routeKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (this.reported) return;
    this.reported = true;
    console.error("[PageErrorBoundary] Page crashed:", error.message);
    captureError("react.page", error, {
      route: this.props.routeKey,
      metadata: { componentStack: info.componentStack?.slice(0, 400) },
    });
  }

  private retry = () => {
    this.reported = false;
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
          <AlertCircle className="h-10 w-10 text-muted-foreground" />
          <div>
            <p className="text-lg font-semibold text-foreground">Something went wrong on this screen</p>
            <p className="mt-1 text-sm text-muted-foreground">Nothing has been lost. Try again, or go back and open it once more.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={this.retry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try again
            </Button>
            <Button
              variant="outline"
              onClick={() => (window.history.length > 1 ? window.history.back() : window.location.assign("/"))}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Go back
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
